import { motion } from "framer-motion";
import { ShieldCheck, Lock, MapPin, BadgeCheck, FileCode, Search } from "lucide-react";

const SecuritySection = () => {
  const features = [
    {
      icon: ShieldCheck,
      title: "No Wallet Access. Ever.",
      description:
        "GHOST never touches your crypto. No wallet connections, no seed phrases, no private keys. Your funds stay with you.",
    },
    {
      icon: Lock,
      title: "Encrypted by Default",
      description:
        "All data encrypted in transit (TLS 1.3) and at rest (AES-256). Your strategies, watchlists, and history are yours alone.",
    },
    {
      icon: MapPin,
      title: "Built in Australia",
      description:
        "Developed by Future Gen AI — an Australian technology company. Real team, real roadmap, real accountability.",
    },
  ];

  const badges = [
    { icon: BadgeCheck, label: "SOC 2 Compliance", status: "In Progress" },
    { icon: FileCode, label: "Open-Source Risk Engine", status: "Q3 2026" },
    { icon: Search, label: "Independent Security Audit", status: "Scheduled" },
  ];

  return (
    <section
      id="security"
      className="relative py-16 md:py-24 overflow-hidden"
      data-testid="security-section"
    >
      {/* Background image overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1728739529355-31dcaefd82b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwY3J5cHRvJTIwdHJhZGluZyUyMG5lb24lMjBibHVlfGVufDB8fHx8MTc3NTc4MDk1OHww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16"
        >
          <span className="inline-block px-3 md:px-4 py-2 rounded-full glass text-xs md:text-sm font-mono text-[#00D4FF] mb-3 md:mb-4">
            SECURITY
          </span>
          <h2 className="font-['Unbounded'] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-3 md:mb-4">
            Your Capital. Your Control.
            <br />
            <span className="gradient-text">Always.</span>
          </h2>
          <p className="text-[#A1A1AA] text-base md:text-lg">
            Security isn't a feature — it's the foundation.
          </p>
        </motion.div>

        {/* Security features grid - Responsive */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 mb-10 md:mb-16">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="p-6 md:p-8 rounded-2xl md:rounded-3xl glass ice-hover text-center"
              data-testid={`security-feature-${index}`}
            >
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-[#00D4FF]/10 border border-[#00D4FF]/30 flex items-center justify-center mx-auto mb-4 md:mb-6">
                <feature.icon size={28} className="text-[#00D4FF] md:w-8 md:h-8" />
              </div>
              <h3 className="font-['Unbounded'] text-lg md:text-xl font-bold mb-2 md:mb-3 text-white">
                {feature.title}
              </h3>
              <p className="text-[#A1A1AA] leading-relaxed text-sm md:text-base">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Trust badges - Responsive wrap */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-wrap justify-center gap-3 md:gap-4"
        >
          {badges.map((badge, index) => (
            <div
              key={badge.label}
              className="flex items-center gap-2 md:gap-3 px-4 md:px-5 py-2.5 md:py-3 rounded-full glass"
            >
              <badge.icon size={16} className="text-[#00D4FF] md:w-[18px] md:h-[18px]" />
              <span className="text-xs md:text-sm font-medium text-white">
                {badge.label}
              </span>
              <span className="text-[10px] md:text-xs font-mono text-[#8B5CF6]">
                {badge.status}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default SecuritySection;
